import { useState } from "react";
import { useServices } from "../../lib/queries";
import { useCockpit } from "../../lib/state";
import { relativeTime } from "../../lib/format";

const STATUS_COLOR: Record<string, string> = {
  healthy: "#7ec699",
  warning: "#d4a04c",
  at_risk: "#cf6679",
  unknown: "var(--plum)",
};

const LOGIN_LABEL: Record<string, string> = {
  google_oauth: "G",
  github_oauth: "GH",
  apple_id: "",
  email_password: "@",
  sso: "SSO",
  magic_link: "✉",
  api_key: "key",
};

export default function ServicesPane() {
  const { data: services = [] } = useServices();
  const openDrawer = useCockpit((s) => s.openDrawer);
  const [filter, setFilter] = useState("");

  const matched = services.filter((s) => {
    if (!filter) return true;
    const q = filter.toLowerCase();
    return (
      s.name.toLowerCase().includes(q) ||
      (s.category ?? "").toLowerCase().includes(q) ||
      (s.account_email ?? "").toLowerCase().includes(q) ||
      (s.used_by ?? []).some((u) => u.toLowerCase().includes(q))
    );
  });

  return (
    <div className="aero-glass mx-3 mt-3 mb-4 flex flex-col" style={{ borderRadius: 22 }}>
      <div
        className="flex items-center justify-between px-4 py-3"
        style={{ borderBottom: "1px solid rgba(255,31,165,0.15)" }}
      >
        <div className="flex items-center gap-3">
          <span
            className="text-lg"
            style={{
              fontFamily: "Fraunces, serif",
              fontStyle: "italic",
              fontWeight: 700,
            }}
          >
            <span className="holo-text">Services</span>
          </span>
          <span
            className="font-eyebrow"
            style={{
              fontSize: 10,
              fontWeight: 700,
              letterSpacing: "0.16em",
              color: "var(--plum)",
              opacity: 0.6,
            }}
          >
            · {matched.length}
          </span>
        </div>
        <input
          value={filter}
          onChange={(e) => setFilter(e.target.value)}
          placeholder="filter…"
          className="px-3 py-2 outline-none"
          style={{
            background: "rgba(255,255,255,0.55)",
            border: "1px solid rgba(255,255,255,0.7)",
            borderRadius: 12,
            fontFamily: "Manrope, sans-serif",
            fontSize: 12,
            color: "var(--ink)",
            width: 200,
            boxShadow: "inset 0 1px 0 rgba(255,255,255,.85)",
          }}
        />
      </div>
      <div
        className="grid grid-cols-12 gap-0 px-4 py-2"
        style={{ borderBottom: "1px solid rgba(255,31,165,0.1)" }}
      >
        {["Name", "Category", "Login", "Status", "Renews", "$/mo", "Checked"].map((h, i) => {
          const cols = [3, 2, 1, 1, 2, 1, 2][i];
          const align = h === "$/mo" ? "text-right" : "";
          return (
            <span
              key={h}
              className={`font-eyebrow col-span-${cols} ${align}`}
              style={{
                fontSize: 9,
                fontWeight: 800,
                letterSpacing: "0.16em",
                textTransform: "uppercase",
                color: "var(--hot-pink)",
                opacity: 0.85,
                gridColumn: `span ${cols} / span ${cols}`,
              }}
            >
              {h}
            </span>
          );
        })}
      </div>
      <div className="flex flex-col gap-1 p-2">
        {matched.map((s) => (
          <button
            key={s.id}
            type="button"
            onClick={() => openDrawer("service", s.id)}
            className="aero-glass aero-glass-tight grid grid-cols-12 items-center gap-0 px-3 py-2 text-left transition-transform hover:-translate-y-0.5"
            style={{ borderRadius: 12 }}
          >
            <span
              className="col-span-3 truncate"
              style={{
                fontFamily: "Fraunces, serif",
                fontStyle: "italic",
                fontWeight: 600,
                fontSize: 13,
                color: "var(--ink)",
              }}
            >
              {s.name}
            </span>
            <span
              className="col-span-2 truncate text-xs"
              style={{ color: "var(--plum)" }}
            >
              {s.category ?? "—"}
            </span>
            <span className="col-span-1 text-xs" style={{ color: "var(--plum)" }}>
              {LOGIN_LABEL[s.login_method ?? ""] ?? "•"}
            </span>
            <span
              className="font-eyebrow col-span-1 inline-flex items-center gap-1"
              style={{
                fontSize: 9,
                fontWeight: 800,
                letterSpacing: "0.1em",
                textTransform: "uppercase",
                color: STATUS_COLOR[s.status] ?? "var(--plum)",
              }}
            >
              <span
                className="glossy-radial-bubble"
                style={{
                  width: 7,
                  height: 7,
                  color: STATUS_COLOR[s.status] ?? "var(--plum)",
                }}
              />
              {s.status}
            </span>
            <span
              className="font-eyebrow tabular col-span-2"
              style={{ fontSize: 10, color: "var(--plum)", opacity: 0.75 }}
            >
              {s.renews_at ? relativeTime(`${s.renews_at}T00:00:00Z`) : "—"}
            </span>
            <span
              className="tabular col-span-1 text-right"
              style={{
                fontFamily: "Bricolage Grotesque, sans-serif",
                fontWeight: 800,
                fontSize: 11,
                color: "var(--hot-pink)",
              }}
            >
              {(s.monthly_cost_usd ?? 0) > 0 ? `$${Math.round(s.monthly_cost_usd)}` : "—"}
            </span>
            <span
              className="font-eyebrow tabular col-span-2"
              style={{ fontSize: 10, color: "var(--plum)", opacity: 0.55 }}
            >
              {s.last_checked_at ? relativeTime(s.last_checked_at) : "never"}
            </span>
          </button>
        ))}
      </div>
    </div>
  );
}
